import { PlexRoute } from '@all-hands-hq/plex-graphql-schema';
import isDeepEqual from 'lodash/isEqual';
import { useCallback, useMemo } from 'react';
import { createSelectorCreator, defaultMemoize } from 'reselect';

import { FilterPublishStatus, TasksOrder, TaskType } from '../../graphql/gen/types';
import { usePlexRoute } from '../../views/logged_in/details/usePlexRoute';
import { Action } from './actions';
import { filtersReducer } from './filtersReducer';
import { FiltersState } from './filtersState';

export type Dispatch = (action: Action) => void;

// Use deep equality check to not re-render whole components which depend on a specific
// query param due to changes in _any_ part of the URL.
// TODO(https://github.com/all-hands-hq/source/issues/1924): Do this for `usePlexRoute`
const createSelector = createSelectorCreator(defaultMemoize, isDeepEqual);

const selectLabelIds = createSelector(
  (plexRoute: PlexRoute | null) => plexRoute?.filters?.labelIds,
  (filterPlexIds) => filterPlexIds || []
);

const selectPlexPermalinks = createSelector(
  (plexRoute: PlexRoute | null) => plexRoute?.filters?.plexIds,
  (filterPlexIds) => filterPlexIds || []
);

const selectPublishStatusFilter = createSelector(
  (plexRoute: PlexRoute | null) => plexRoute?.filters?.publishStatus,
  (filterPublishStatus) => toFilterPublishStatus(filterPublishStatus)
);

const selectPlexSummaryPermalink = createSelector(
  (plexRoute: PlexRoute | null) => plexRoute?.plexSummary?.permalink,
  (plexId) => plexId
);

const selectTasksItemType = createSelector(
  (plexRoute: PlexRoute | null) => plexRoute?.tasksFilters?.itemType,
  (itemType) => toTaskType(itemType)
);

const selectTasksOrder = createSelector(
  (plexRoute: PlexRoute | null) => plexRoute?.tasksFilters?.order,
  (order) => toTasksOrder(order)
);

export function useFiltersState(): [FiltersState, Dispatch] {
  const [plexRoute, setPlexRoute] = usePlexRoute();

  const labelIds = selectLabelIds(plexRoute);
  const selectedPlexPermalinks = selectPlexPermalinks(plexRoute);
  const publishStatusFilter = selectPublishStatusFilter(plexRoute);
  const plexSummaryPermalink = selectPlexSummaryPermalink(plexRoute);
  const tasksItemType = selectTasksItemType(plexRoute);
  const tasksOrder = selectTasksOrder(plexRoute);

  const state: FiltersState = useMemo(
    () => ({
      labelIds,
      selectedPlexPermalinks,
      publishStatusFilter,
      tasks: {
        itemType: tasksItemType,
        order: tasksOrder,
      },
      router: {
        plexSummaryPermalink,
      },
    }),
    [labelIds, plexSummaryPermalink, publishStatusFilter, selectedPlexPermalinks, tasksItemType, tasksOrder]
  );

  const dispatch = useCallback(
    (action: Action): void => {
      const nextState = filtersReducer(state, action);

      setPlexRoute(
        {
          filters: {
            ...plexRoute.filters,
            labelIds: nextState.labelIds,
            plexIds: nextState.selectedPlexPermalinks,
            ...(nextState.publishStatusFilter !== FilterPublishStatus.All
              ? { publishStatus: nextState.publishStatusFilter }
              : { publishStatus: undefined }),
          },
          ...(nextState.router.plexSummaryPermalink != null
            ? {
                plexSummary: { permalink: nextState.router.plexSummaryPermalink },
              }
            : { plexSummary: undefined }),
          ...(nextState.tasks.itemType != null || nextState.tasks.order != null
            ? {
                tasksFilters: { itemType: nextState.tasks.itemType, order: nextState.tasks.order },
              }
            : { tasksFilters: undefined }),
        },
        'replace'
      );
    },
    [plexRoute.filters, setPlexRoute, state]
  );

  return [state, dispatch];
}

const toFilterPublishStatus = (filterPublishStatus: PlexRoute['filters']['publishStatus']): FilterPublishStatus => {
  switch (filterPublishStatus) {
    case 'DRAFT':
      return FilterPublishStatus.Draft;
    case 'INTERNAL':
      return FilterPublishStatus.Internal;
    case 'PUBLISHED':
      return FilterPublishStatus.Published;
    default:
      return FilterPublishStatus.All;
  }
};

const toTaskType = (taskType: NonNullable<PlexRoute['tasksFilters']>['itemType']): TaskType | undefined => {
  switch (taskType) {
    case 'MILESTONE':
      return TaskType.Milestone;
    case 'TASK':
      return TaskType.Task;

    default:
      return undefined;
  }
};

const toTasksOrder = (tasksOrder: NonNullable<PlexRoute['tasksFilters']>['order']): TasksOrder => {
  switch (tasksOrder) {
    case 'DUE_DATE_DESC':
      return TasksOrder.DueDateDesc;
    case 'DUE_DATE_ASC':
    case undefined:
      return TasksOrder.DueDateAsc;
    case 'STATUS_ASC':
      return TasksOrder.StatusAsc;
    case 'STATUS_DESC':
      return TasksOrder.StatusDesc;
    case 'CREATED_AT_DESC':
      return TasksOrder.CreatedAtDesc;
    case 'CREATED_AT_ASC':
      return TasksOrder.CreatedAtAsc;
  }
};
